import { ComponentProps, useCallback, useState } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import {
  Content,
  FormatContent,
  useContent,
} from 'src/contexts/ContentContext';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import {
  adjustFxRatePreview,
  AdjustFxRatePreviewResponse,
} from 'src/mocks/adjustFxRate';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { formatCurrency } from 'src/utils/numberFormatter';
import { PurchaseClient, PurchaseOrderDetails } from 'src/WebApiController';

import * as styles from './AdjustFxRateDialog.css';

export type AdjustFxRateDialogProps = Omit<
  ComponentProps<typeof GenericDialog>,
  'header' | 'footer'
> & {
  purchaseOrder: PurchaseOrderDetails;
  onSuccess: () => void;
  onCancel: () => void;
};

export const AdjustFxRateDialog = ({
  purchaseOrder,
  onSuccess,
  onCancel,
  ...dialogProps
}: AdjustFxRateDialogProps) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();

  const currentFxRate = purchaseOrder.conversionRate ?? 1.0;
  // originalCurrencyCode is the pre-conversion currency (non-null when dialog is shown).
  // currency is the post-conversion (target/account) currency.
  const originalCurrency =
    purchaseOrder.originalCurrencyCode ?? purchaseOrder.currency;
  const targetCurrency = purchaseOrder.currency;

  const [newFxRateInput, setNewFxRateInput] = useState<string>('');
  const [preview, setPreview] = useState<AdjustFxRatePreviewResponse | null>(
    null
  );
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [inputError, setInputError] = useState<string | null>(null);

  const invalidFxRateMsg = useContent(ContentId.InvalidFxRateError);
  const failedToUpdateMsg = useContent(ContentId.FailedToUpdateFxRate);
  const newFxRateLabel = useContent(ContentId.NewFxRate);
  const newFxRatePlaceholder = useContent(ContentId.NewFxRatePlaceholder);

  const parsedFxRate = parseFloat(newFxRateInput);
  const isValidRate = !isNaN(parsedFxRate) && parsedFxRate > 0;

  const handlePreview = useCallback(async () => {
    if (!isValidRate) {
      setInputError(invalidFxRateMsg);
      return;
    }
    setInputError(null);
    setIsPreviewLoading(true);
    try {
      const result = await adjustFxRatePreview(
        { purchaseOrderId: purchaseOrder.id, newFxRate: parsedFxRate },
        purchaseOrder
      );
      setPreview(result);
    } catch (e) {
      showErrorDialog('adjustFxRatePreview', e as Error, {
        trackErrorData: { purchaseOrderId: purchaseOrder.id },
      });
    } finally {
      setIsPreviewLoading(false);
    }
  }, [isValidRate, parsedFxRate, purchaseOrder, showErrorDialog]);

  const handleApply = useCallback(async () => {
    if (!isValidRate || preview == null) return;
    setIsApplying(true);
    try {
      const succeeded = await new PurchaseClient(
        activeAccountWebClientConfig
      ).updatePurchaseOrderConversionRate({
        item1: purchaseOrder.id,
        item2: parsedFxRate,
      });
      if (succeeded) {
        onSuccess();
      } else {
        showErrorDialog(
          'updatePurchaseOrderConversionRate',
          new Error(failedToUpdateMsg),
          { trackErrorData: { purchaseOrderId: purchaseOrder.id } }
        );
      }
    } catch (e) {
      showErrorDialog('updatePurchaseOrderConversionRate', e as Error, {
        trackErrorData: { purchaseOrderId: purchaseOrder.id },
      });
    } finally {
      setIsApplying(false);
    }
  }, [
    activeAccountWebClientConfig,
    isValidRate,
    parsedFxRate,
    preview,
    purchaseOrder,
    onSuccess,
    showErrorDialog,
  ]);

  const isLoading = isPreviewLoading || isApplying;

  const footer = (
    <Stack direction="row" gap="m" justifyContent="end">
      <Button variant="outline" onClick={onCancel} disabled={isLoading}>
        <Content id={ContentId.Cancel} />
      </Button>
      <Button
        variant="outline"
        onClick={handlePreview}
        disabled={isLoading || !isValidRate}
      >
        <Content id={ContentId.Preview} />
      </Button>
      {isApplying ? (
        <PosSpinner size={vars.iconSize.xxs} />
      ) : (
        <Button
          onClick={handleApply}
          disabled={isLoading || preview == null || !isValidRate}
        >
          <Content id={ContentId.Apply} />
        </Button>
      )}
    </Stack>
  );

  return (
    <GenericDialog
      {...dialogProps}
      size="m"
      header={
        <FormatContent
          id={FormatContentId.AdjustFxRatePurchaseOrder}
          params={[`${purchaseOrder.id}`]}
        />
      }
      footer={footer}
      onCancel={onCancel}
    >
      {isApplying ? (
        <PosSpinner />
      ) : (
        <Stack direction="column" gap="m">
          {/* Read-only PO info */}
          <div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>
                <Content id={ContentId.OriginalCurrency} />
              </span>
              <span className={styles.infoValue}>{originalCurrency}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>
                <Content id={ContentId.TargetCurrency} />
              </span>
              <span className={styles.infoValue}>{targetCurrency}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>
                <Content id={ContentId.CurrentFxRate} />
              </span>
              <span className={styles.infoValue}>
                {currentFxRate.toFixed(6)}
              </span>
            </div>
          </div>

          {/* New FX Rate input */}
          <div className={styles.inputSection}>
            <PosFormField
              label={newFxRateLabel}
              errors={inputError ?? undefined}
            >
              <PosTextField
                type="number"
                step="0.000001"
                min="0.000001"
                placeholder={newFxRatePlaceholder}
                value={newFxRateInput}
                onChange={(e) => {
                  setNewFxRateInput(e.target.value);
                  setPreview(null);
                  setInputError(null);
                }}
                disabled={isLoading}
              />
            </PosFormField>
          </div>

          {/* Preview section */}
          {isPreviewLoading && <PosSpinner />}

          {preview != null && !isPreviewLoading && (
            <div className={styles.divider}>
              <div className={styles.sectionTitle}>
                <Content id={ContentId.CostPreview} />
              </div>

              {/* PO-level summary */}
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>
                  <Content id={ContentId.PoTotalCost} />
                </span>
                <span className={styles.infoValue}>
                  {formatCurrency(
                    preview.currentPoCosts.totalCost,
                    targetCurrency,
                    2
                  )}
                  {' → '}
                  <span className={styles.newValueHighlight}>
                    {formatCurrency(
                      preview.previewPoCosts.totalCost,
                      targetCurrency,
                      2
                    )}
                  </span>
                </span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>
                  <Content id={ContentId.PoUnitCost} />
                </span>
                <span className={styles.infoValue}>
                  {formatCurrency(
                    preview.currentPoCosts.unitCost,
                    targetCurrency,
                    2
                  )}
                  {' → '}
                  <span className={styles.newValueHighlight}>
                    {formatCurrency(
                      preview.previewPoCosts.unitCost,
                      targetCurrency,
                      2
                    )}
                  </span>
                </span>
              </div>

              {/* Per-ticket-group table */}
              {preview.ticketGroupPreviews.length > 0 && (
                <>
                  <div className={styles.sectionTitle}>
                    <Content id={ContentId.TicketGroupBreakdown} />
                  </div>
                  <table className={styles.previewTable}>
                    <thead>
                      <tr>
                        <th className={styles.previewTableHeader}>
                          <Content id={ContentId.Event} />
                        </th>
                        <th className={styles.previewTableHeader}>
                          <Content id={ContentId.Quantity} />
                        </th>
                        <th className={styles.previewTableHeader}>
                          <Content id={ContentId.UnitCost} />
                        </th>
                        <th className={styles.previewTableHeader}>
                          <Content id={ContentId.FLUC} />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.ticketGroupPreviews.map((tg) => (
                        <tr key={tg.ticketGroupId}>
                          <td className={styles.previewTableCell}>
                            {tg.eventDescription}
                          </td>
                          <td className={styles.previewTableCell}>
                            {tg.quantity}
                          </td>
                          <td className={styles.previewTableCell}>
                            {formatCurrency(
                              tg.currentUnitCost,
                              targetCurrency,
                              2
                            )}
                            {' → '}
                            <span className={styles.newValueHighlight}>
                              {formatCurrency(
                                tg.newUnitCost,
                                targetCurrency,
                                2
                              )}
                            </span>
                          </td>
                          <td className={styles.previewTableCell}>
                            {formatCurrency(tg.currentFluc, targetCurrency, 2)}
                            {' → '}
                            <span className={styles.newValueHighlight}>
                              {formatCurrency(tg.newFluc, targetCurrency, 2)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          )}
        </Stack>
      )}
    </GenericDialog>
  );
};
